/**
 * PrestigeSystemWrapper - Manages prestige/ascension operations with EventBus integration
 *
 * Wraps PrestigeStateManager methods and emits events for cross-system communication.
 * Part of SYS-04 requirement from Phase 5 system extraction.
 *
 * This follows the Strangler Fig pattern established in Phase 3/4:
 * - PrestigeSystemWrapper wraps PrestigeStateManager methods (doesn't replace them)
 * - Adds EventBus integration for decoupled communication
 * - Enables testability through dependency injection
 *
 * Named "PrestigeSystemWrapper" to avoid conflict with existing PrestigeSystem.js
 * (which contains pure utility functions for prestige calculations).
 *
 * Usage:
 *   import { PrestigeSystemWrapper, getPrestigeSystemWrapper } from './systems/PrestigeSystemWrapper.js';
 *
 *   // Create instance with dependency injection
 *   const prestigeSystem = new PrestigeSystemWrapper(gameState);
 *
 *   // Or use singleton pattern
 *   const prestigeSystem = getPrestigeSystemWrapper(gameState);
 *
 *   // Perform prestige (emits PRESTIGE_PERFORMED event on success)
 *   prestigeSystem.performPrestige('baseSpeed');
 */

import { eventBus, GameEvents } from '../utils/EventBus.js';

export class PrestigeSystemWrapper {
    /**
     * @param {GameState} gameState - Reference to main game state
     */
    constructor(gameState) {
        this.gameState = gameState;
    }

    /**
     * Get the prestige state manager (shorthand)
     */
    get prestigeStateManager() {
        return this.gameState.prestigeStateManager;
    }

    // ===== Read Operations (Delegation) =====

    /**
     * Get current prestige level
     * @returns {number} Current prestige level (0 = never prestiged)
     */
    getPrestigeLevel() {
        return this.prestigeStateManager.getPrestigeLevel();
    }

    /**
     * Get mile earning bonus from prestige
     * @returns {number} Mile earning bonus as decimal (0.15 = 15% bonus)
     */
    getMileEarningBonus() {
        return this.prestigeStateManager.getMileEarningBonus();
    }

    /**
     * Get skill bonus for a specific skill type
     * @param {string} skillType - Skill type (e.g., 'baseSpeed', 'acceleration')
     * @returns {number} Skill bonus as decimal (0.15 = 15% bonus)
     */
    getSkillBonus(skillType) {
        return this.prestigeStateManager.getSkillBonus(skillType);
    }

    /**
     * Get all skill bonuses as a copy (not reference)
     * @returns {object} Copy of skill bonuses object
     */
    getAllSkillBonuses() {
        return this.prestigeStateManager.getAllSkillBonuses();
    }

    /**
     * Check if player can prestige (is on final car)
     * @returns {boolean} True if prestige is available
     */
    canPrestige() {
        return this.prestigeStateManager.canPrestige();
    }

    /**
     * Get prestige preview info including bonuses that would be gained
     * @returns {object} Prestige info with level, bonuses, and preview
     */
    getPrestigeInfo() {
        return this.prestigeStateManager.getPrestigeInfo();
    }

    /**
     * Get level multiplier for current prestige level
     * @returns {number} Multiplier (1.0, 1.25, 1.5, etc.)
     */
    getLevelMultiplier() {
        return this.prestigeStateManager.getLevelMultiplier();
    }

    /**
     * Get selectable skills for prestige
     * @returns {object} SELECTABLE_SKILLS from PrestigeSystem
     */
    getSelectableSkills() {
        return this.prestigeStateManager.getSelectableSkills();
    }

    // ===== Write Operations (with Event Emission) =====

    /**
     * Perform prestige - reset progress and gain permanent bonuses
     * Emits GameEvents.PRESTIGE_PERFORMED on success
     * @param {string} selectedSkill - The skill to apply the bonus to
     * @returns {object|false} Result object with new prestige info, or false if failed
     */
    performPrestige(selectedSkill) {
        // Get level before prestige for event data
        const levelBefore = this.getPrestigeLevel();

        // Delegate to PrestigeStateManager (which delegates to GameState)
        const result = this.prestigeStateManager.performPrestige(selectedSkill);

        // Emit event on success
        if (result !== false) {
            eventBus.emit(GameEvents.PRESTIGE_PERFORMED, {
                level: levelBefore + 1,
                selectedSkill: selectedSkill,
                bonusGained: result.bonusGained || 0
            });
        }

        return result;
    }
}

// =============================================================================
// SINGLETON PATTERN
// =============================================================================

let _instance = null;

/**
 * Get or create PrestigeSystemWrapper singleton
 * @param {GameState} [gameState] - GameState reference (required on first call)
 * @returns {PrestigeSystemWrapper|null} PrestigeSystemWrapper instance, or null if not initialized
 */
export function getPrestigeSystemWrapper(gameState) {
    if (!_instance && gameState) {
        _instance = new PrestigeSystemWrapper(gameState);
    }
    return _instance;
}

/**
 * Reset singleton instance (for testing)
 */
export function resetPrestigeSystemWrapper() {
    _instance = null;
}
